import React from 'react';
import { styled, keyframes } from '@mui/material/styles';
import { Box, Typography, Button, Switch, Avatar, Tooltip } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// ============================================
// Help Tooltip
// ============================================
export const HelpTooltip = ({ title }) => (
  <Tooltip
    title={<span style={{ fontSize: '13px', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{title}</span>}
    placement="top"
    arrow
    componentsProps={{
      tooltip: {
        sx: {
          backgroundColor: '#333',
          color: '#fff',
          maxWidth: 320,
          padding: '10px 14px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
      },
      arrow: { sx: { color: '#333' } },
    }}
  >
    <HelpOutlineIcon sx={{ fontSize: 16, color: '#B0B0B0', cursor: 'help', ml: '4px', '&:hover': { color: '#4AB3BC' } }} />
  </Tooltip>
);

// ============================================
// Error Animation & Wrapper
// ============================================
export const errorPulseGlow = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); }
  50% { box-shadow: 0 0 0 6px rgba(220, 38, 38, 0); }
  100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
`;

export const ErrorFieldWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'hasError',
})(({ hasError }) => ({
  borderRadius: '4px',
  ...(hasError && {
    '& input': {
      borderColor: '#dc2626 !important',
      borderWidth: '2px !important',
    },
    animation: `${errorPulseGlow} 1.5s ease-in-out infinite`,
  }),
}));

export const ErrorText = styled(Typography)({
  fontSize: '12px',
  color: '#dc2626',
  marginTop: '4px',
});

export const EmptyRangeHint = styled(Typography)({
  fontSize: '13px',
  color: '#8c8c8c',
  padding: '8px 0',
});

// ============================================
// Page Layout
// ============================================
export const PageWrapper = styled(Box)({
  display: 'flex',
  gap: '32px',
  padding: '32px',
  minHeight: 'calc(100vh - 80px)',
  backgroundColor: '#F4F6F9',
});

export const LeftPanel = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  width: '400px',
  flexShrink: 0,
  borderRight: '1px solid #E8E8E8',
  paddingRight: '24px',
});

export const RightPanel = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  flex: 1,
  minWidth: 0,
});

// ============================================
// Card (공통 섹션 카드)
// ============================================
export const Card = styled(Box)({
  backgroundColor: '#fff',
  borderRadius: '16px',
  padding: '24px',
  boxShadow: '0px 0px 8px 0px rgba(0, 0, 0, 0.08)',
});

export const ContentCard = styled(Box)({
  backgroundColor: '#F4F6F9',
  borderRadius: '16px',
  padding: '24px',
  boxShadow: '0px 0px 8px 0px rgba(0, 0, 0, 0.08)',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

// ============================================
// Section Title
// ============================================
export const SectionTitle = styled(Typography)({
  fontSize: '24px',
  fontWeight: 700,
  color: '#4AB3BC',
});

export const SectionTitleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

export const SectionSubText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#A6A6A6',
});

// ============================================
// 왼쪽 패널 - 직원 목록
// ============================================
export const SearchRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

export const SearchField = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 16px',
  width: '240px',
  height: '44px',
  backgroundColor: '#fff',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  boxSizing: 'border-box',
});

export const ToggleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const ToggleLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#404040',
});

export const PositionGroupItem = styled(Box, {
  shouldForwardProp: prop => prop !== 'isExpanded' && prop !== 'isSelected',
})(({ isExpanded, isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 16px',
  backgroundColor: '#fff',
  border: `1px solid ${isExpanded ? '#4AB3BC' : '#D9D9D9'}`,
  borderRadius: '16px',
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#4AB3BC',
  },
}));

export const PositionGroupTitle = styled(Typography)({
  fontSize: '20px',
  fontWeight: 600,
  color: '#333',
});

export const EmployeeItem = styled(Box, {
  shouldForwardProp: prop => prop !== 'isSelected',
})(({ isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '16px',
  backgroundColor: isSelected ? '#F3F9FA' : '#F2F2F2',
  border: `1px solid ${isSelected ? '#4AB3BC' : '#D9D9D9'}`,
  borderRadius: '8px',
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#4AB3BC',
  },
}));

export const EmployeeAvatar = styled(Avatar)({
  width: '48px',
  height: '48px',
  backgroundColor: '#D9D9D9',
  flexShrink: 0,
});

export const EmployeeName = styled(Typography)({
  fontSize: '18px',
  fontWeight: 600,
  color: '#404040',
});

export const EmployeePhone = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#737373',
});

export const StatusBadge = styled(Box, {
  shouldForwardProp: prop => prop !== 'type',
})(({ type }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '0 10px',
  height: '24px',
  borderRadius: '4px',
  fontSize: '14px',
  fontWeight: 600,
  color: '#fff',
  backgroundColor: type === 'active' ? '#55C982' : '#A6A6A6',
}));

export const RetiredTag = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#A6A6A6',
});

// ============================================
// 급여산정 기간
// ============================================
export const PeriodRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
});

export const PeriodSelect = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  width: '80px',
  height: '44px',
  backgroundColor: '#fff',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  cursor: 'pointer',
  boxSizing: 'border-box',
});

export const PeriodLabel = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#737373',
});

export const PeriodNote = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#A6A6A6',
});

export const SaveBadge = styled(Button)({
  padding: '4px 16px',
  backgroundColor: '#4AB3BC',
  color: '#fff',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#3a9da5',
  },
});

// ============================================
// 프로필 대시보드
// ============================================
export const ProfileCard = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  backgroundColor: '#fff',
  borderRadius: '16px',
  padding: '16px 24px',
});

export const ProfileAvatar = styled(Avatar)({
  width: '48px',
  height: '48px',
  borderRadius: '25px',
  flexShrink: 0,
  backgroundColor: '#D9D9D9',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
});

export const ProfileName = styled(Typography)({
  fontSize: '20px',
  fontWeight: 600,
  color: '#404040',
});

export const ProfileStatus = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#737373',
});

export const ProfileInfoRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap',
});

export const ProfileInfoItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '4px',
});

export const ProfileInfoText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#A6A6A6',
});

// ============================================
// 급여 타입 선택
// ============================================
export const SalaryTypeCard = styled(Box, {
  shouldForwardProp: prop => prop !== 'isSelected',
})(({ isSelected }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '24px',
  backgroundColor: isSelected ? '#F3F9FA' : '#fff',
  border: `1px solid ${isSelected ? '#4AB3BC' : '#D9D9D9'}`,
  borderRadius: '16px',
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#4AB3BC',
  },
}));

export const SalaryTypeIcon = styled(Box, {
  shouldForwardProp: prop => prop !== 'bgColor',
})(({ bgColor }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '48px',
  height: '48px',
  borderRadius: '8px',
  backgroundColor: bgColor || 'rgba(85, 201, 129, 0.16)',
}));

export const SalaryTypeName = styled(Typography)({
  fontSize: '20px',
  fontWeight: 600,
  color: '#333',
});

export const SalaryTypeDesc = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  flexWrap: 'wrap',
});

export const SalaryTypeDescItem = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#A6A6A6',
});

export const DotSeparator = styled(Box)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#A6A6A6',
  lineHeight: 1,
});

// ============================================
// Form Field
// ============================================
export const FormRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const FormFieldGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '280px',
});

export const FormFieldLabel = styled(Typography, {
  shouldForwardProp: prop => prop !== 'required',
})(({ required }) => ({
  fontSize: '18px',
  fontWeight: 600,
  color: '#333',
  ...(required && {
    '&::after': {
      content: '" *"',
      color: '#CF1322',
      fontSize: '16px',
      fontWeight: 700,
    },
  }),
}));

export const FormFieldDesc = styled(Typography)({
  fontSize: '12px',
  fontWeight: 500,
  color: '#A6A6A6',
});

export const InputBox = styled(Box, {
  shouldForwardProp: prop => prop !== 'inputWidth',
})(({ inputWidth }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '8px',
  padding: '12px 16px',
  width: inputWidth || '256px',
  height: '44px',
  backgroundColor: '#F4F6F9',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  boxSizing: 'border-box',
}));

export const InputBoxText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'hasValue',
})(({ hasValue }) => ({
  fontSize: '16px',
  fontWeight: 500,
  color: hasValue ? '#333' : '#A6A6A6',
  flex: 1,
  textAlign: 'right',
}));

export const InputUnit = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#737373',
});

export const SelectBox = styled(Box, {
  shouldForwardProp: prop => prop !== 'selectWidth',
})(({ selectWidth }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  width: selectWidth || '160px',
  height: '44px',
  backgroundColor: '#fff',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  cursor: 'pointer',
  boxSizing: 'border-box',
}));

export const DateInputBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  width: '256px',
  height: '44px',
  backgroundColor: '#F4F6F9',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  boxSizing: 'border-box',
});

export const DateText = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#333',
});

export const CheckRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const CheckLabel = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#737373',
});

export const NoteText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#A6A6A6',
});

export const Divider = styled(Box)({
  height: '1px',
  backgroundColor: '#D9D9D9',
  width: '100%',
});

// ============================================
// 공제 항목
// ============================================
export const DeductionCard = styled(Box, {
  shouldForwardProp: prop => prop !== 'isSelected',
})(({ isSelected }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '4px',
  padding: '16px',
  width: '120px',
  backgroundColor: isSelected ? '#F3F9FA' : '#fff',
  border: `1px solid ${isSelected ? '#4AB3BC' : '#D9D9D9'}`,
  borderRadius: '16px',
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#4AB3BC',
  },
}));

export const DeductionRate = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isSelected',
})(({ isSelected }) => ({
  fontSize: '16px',
  fontWeight: 600,
  color: isSelected ? '#4AB3BC' : '#737373',
}));

export const DeductionLabel = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isSelected',
})(({ isSelected }) => ({
  fontSize: '14px',
  fontWeight: 500,
  color: isSelected ? '#4AB3BC' : '#A6A6A6',
}));

export const DeductionDesc = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#737373',
  '& span': {
    color: '#333',
  },
});

// ============================================
// 수업 수당 설정
// ============================================
export const LessonSettingRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const LessonLabel = styled(Typography, {
  shouldForwardProp: prop => prop !== 'required',
})(({ required }) => ({
  fontSize: '16px',
  fontWeight: 500,
  color: '#737373',
  width: '88px',
  minWidth: '88px',
  ...(required && {
    '&::after': {
      content: '" *"',
      color: '#CF1322',
      fontSize: '14px',
      fontWeight: 700,
    },
  }),
}));

export const RadioRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
  height: '44px',
});

export const RadioItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
});

export const RadioLabel = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#737373',
});

export const RadioCircle = styled(Box, {
  shouldForwardProp: prop => prop !== 'isSelected',
})(({ isSelected }) => ({
  width: '24px',
  height: '24px',
  borderRadius: '12px',
  border: `${isSelected ? '2px' : '1px'} solid ${isSelected ? '#4AB3BC' : '#D9D9D9'}`,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#fff',
  cursor: 'pointer',
}));

export const RadioDot = styled(Box)({
  width: '12px',
  height: '12px',
  borderRadius: '6px',
  backgroundColor: '#4AB3BC',
});

export const AmountRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '38px',
});

export const AmountItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

export const AmountLabel = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#737373',
});

export const SmallInputBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '16px',
  padding: '12px 16px',
  width: '160px',
  height: '44px',
  backgroundColor: '#fff',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  boxSizing: 'border-box',
});

export const UnitSelectBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 16px',
  height: '44px',
  backgroundColor: '#fff',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  cursor: 'pointer',
  boxSizing: 'border-box',
});

// ============================================
// 개별 수당 테이블
// ============================================
export const IndividualHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const IndividualHeaderText = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#737373',
  '& strong': {
    fontWeight: 600,
    color: '#4AB3BC',
  },
});

export const TableWrapper = styled(Box)({
  border: '1px solid #F2F2F2',
  borderRadius: '8px',
  overflow: 'hidden',
});

export const TableHeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  height: '40px',
  backgroundColor: '#F4F6F9',
});

export const TableHeaderCell = styled(Box, {
  shouldForwardProp: prop => prop !== 'cellWidth' && prop !== 'flex',
})(({ cellWidth, flex }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '12px 16px',
  height: '40px',
  width: cellWidth,
  flex: flex,
  borderRight: '1px solid #F2F2F2',
  boxSizing: 'border-box',
  '&:last-child': {
    borderRight: 'none',
  },
}));

export const TableHeaderText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#737373',
});

export const TableBodyRow = styled(Box, {
  shouldForwardProp: prop => prop !== 'isExpanded',
})(({ isExpanded }) => ({
  display: 'flex',
  flexDirection: isExpanded ? 'column' : 'row',
  borderTop: '1px solid #F2F2F2',
  backgroundColor: '#F3F9FA',
}));

export const TableBodyCellRow = styled(Box)({
  display: 'flex',
  alignItems: 'stretch',
  width: '100%',
});

export const TableBodyCell = styled(Box, {
  shouldForwardProp: prop => prop !== 'cellWidth' && prop !== 'flex',
})(({ cellWidth, flex }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '8px 16px',
  width: cellWidth,
  flex: flex,
  borderRight: '1px solid #F2F2F2',
  boxSizing: 'border-box',
  '&:last-child': {
    borderRight: 'none',
  },
}));

export const TableCellText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#333',
  textAlign: 'center',
});

export const IndividualBadge = styled(Box)({
  display: 'inline-flex',
  padding: '4px 8px',
  backgroundColor: '#4AB3BC',
  borderRadius: '999px',
  fontSize: '14px',
  fontWeight: 600,
  color: '#fff',
  marginLeft: '8px',
});

export const SettingButton = styled(Box, {
  shouldForwardProp: prop => prop !== 'variant',
})(({ variant }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '24px',
  height: '24px',
  borderRadius: '4px',
  cursor: 'pointer',
  ...(variant === 'add'
    ? {
        border: '1px solid #A6A6A6',
        color: '#A6A6A6',
        fontSize: '14px',
        fontWeight: 600,
        '&:hover': { borderColor: '#4AB3BC', color: '#4AB3BC' },
      }
    : {
        backgroundColor: '#4AB3BC',
        color: '#fff',
        fontSize: '14px',
        fontWeight: 600,
      }),
}));

// ============================================
// 그룹레슨 구간
// ============================================
export const RangeRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const RangeInputBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  width: '72px',
  height: '44px',
  backgroundColor: '#fff',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  boxSizing: 'border-box',
});

export const RangeSeparator = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#A6A6A6',
});

export const AddRangeButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.8,
  },
});

export const AddRangeText = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#4AB3BC',
});

// ============================================
// 초과 매출 수당
// ============================================
export const OverflowRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
});

export const OverflowLabel = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#737373',
});

// 변경 단가 뒤에 붙는 단위 표시. 고를 값이 하나뿐이라 드롭다운이 아닌 읽기 전용 박스다.
export const UnitSuffixBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  height: '44px',
  padding: '12px 16px',
  fontSize: '16px',
  fontWeight: 500,
  color: '#737373',
  backgroundColor: '#fff',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  boxSizing: 'border-box',
});

export const OverflowInputBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '8px',
  padding: '12px 16px',
  width: '160px',
  height: '44px',
  backgroundColor: '#fff',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  boxSizing: 'border-box',
});

export const OverflowUnitBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 16px',
  height: '44px',
  backgroundColor: '#fff',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  boxSizing: 'border-box',
});

// ============================================
// 성과급
// ============================================
export const BonusRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
});

export const BonusLabel = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#737373',
});

// ============================================
// Footer Buttons
// ============================================
export const FooterBar = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: '8px',
  padding: '24px',
  backgroundColor: '#F4F6F9',
  borderRadius: '16px',
  boxShadow: '0px 0px 8px 0px rgba(0, 0, 0, 0.08)',
});

export const CancelButton = styled(Button)({
  width: '100px',
  height: '48px',
  backgroundColor: '#A6A6A6',
  color: '#fff',
  borderRadius: '8px',
  fontSize: '20px',
  fontWeight: 600,
  textTransform: 'uppercase',
  '&:hover': {
    backgroundColor: '#8a8a8a',
  },
});

export const SubmitButton = styled(Button)({
  width: '240px',
  height: '48px',
  backgroundColor: '#4AB3BC',
  color: '#fff',
  borderRadius: '8px',
  fontSize: '20px',
  fontWeight: 600,
  textTransform: 'uppercase',
  '&:hover': {
    backgroundColor: '#3a9da5',
  },
});

// ============================================
// Toggle Switch (Custom)
// ============================================
export const ToggleSwitch = styled(Switch)({
  width: '40px',
  height: '20px',
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: '2px',
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#4AB3BC',
        opacity: 1,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: '16px',
    height: '16px',
  },
  '& .MuiSwitch-track': {
    borderRadius: '10px',
    backgroundColor: '#D9D9D9',
    opacity: 1,
  },
});

// ============================================
// Expanded Setting Panel
// ============================================
export const ExpandedSettingPanel = styled(Box)({
  padding: '16px',
  backgroundColor: '#fff',
  border: '1px solid #F2F2F2',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

// ============================================
// Section Content Layout
// ============================================
export const SectionContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

export const FieldRow = styled(Box)({
  display: 'flex',
  gap: '16px',
});

export const FieldBlock = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

export const ToggleFieldRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

// ============================================
// 월급제 수당 설정 - 배율 버튼
// ============================================
export const RateButtonGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const RateButton = styled(Box, {
  shouldForwardProp: prop => prop !== 'isSelected',
})(({ isSelected }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '8px 20px',
  height: '40px',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer',
  boxSizing: 'border-box',
  backgroundColor: isSelected ? '#4AB3BC' : '#fff',
  color: isSelected ? '#fff' : '#737373',
  border: `1px solid ${isSelected ? '#4AB3BC' : '#D9D9D9'}`,
  '&:hover': {
    borderColor: '#4AB3BC',
  },
}));

export const OvertimeRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap',
});

// ============================================
// Real Input (실제 입력 필드)
// ============================================
export const RealInput = styled('input', {
  shouldForwardProp: prop => prop !== 'inputWidth' && prop !== 'textAlign',
})(({ inputWidth, textAlign }) => ({
  width: inputWidth || '100%',
  height: '44px',
  padding: '12px 16px',
  fontSize: '16px',
  fontWeight: 500,
  fontFamily: 'Pretendard, sans-serif',
  color: '#333',
  backgroundColor: '#fff',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  boxSizing: 'border-box',
  textAlign: textAlign || 'right',
  outline: 'none',
  '&:focus': {
    borderColor: '#4AB3BC',
  },
  '&::placeholder': {
    color: '#A6A6A6',
  },
  // 숫자 스피너 숨기기
  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  MozAppearance: 'textfield',
}));

export const RealSelect = styled('select')({
  height: '44px',
  padding: '12px 16px',
  fontSize: '16px',
  fontWeight: 500,
  fontFamily: 'Pretendard, sans-serif',
  color: '#333',
  backgroundColor: '#fff',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  boxSizing: 'border-box',
  outline: 'none',
  cursor: 'pointer',
  appearance: 'auto',
  '&:focus': {
    borderColor: '#4AB3BC',
  },
});

// ============================================
// Unit Select (원/% 드롭다운)
// ============================================
export const UnitSelect = styled('select')({
  height: '44px',
  padding: '8px 12px',
  fontSize: '16px',
  fontWeight: 500,
  fontFamily: 'Pretendard, sans-serif',
  color: '#737373',
  backgroundColor: '#fff',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  boxSizing: 'border-box',
  outline: 'none',
  cursor: 'pointer',
  appearance: 'auto',
  '&:focus': {
    borderColor: '#4AB3BC',
  },
});

// ============================================
// Checkbox
// ============================================
// 계산에 반영되지 않는 설정을 조작 불가로 흐리게 보여주는 래퍼.
// 값 자체는 그대로 보여줘서 기존에 무엇이 저장돼 있는지는 확인할 수 있게 한다.
// userSelect 는 넣지 않는다 — 조작은 pointerEvents 로 이미 막히고,
// 선택까지 막으면 화면 내용을 복사해 공유할 때 이 블록만 빠진다.
export const DisabledSettingRow = styled(Box)({
  opacity: 0.45,
  pointerEvents: 'none',
});

export const DisabledNote = styled(Typography)({
  fontSize: '12px',
  color: '#888',
  lineHeight: 1.6,
  marginTop: '6px',
});

export const CheckboxCircle = styled(Box, {
  shouldForwardProp: prop => prop !== 'isChecked',
})(({ isChecked }) => ({
  width: '24px',
  height: '24px',
  borderRadius: '4px',
  border: `1px solid ${isChecked ? '#4AB3BC' : '#D9D9D9'}`,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  backgroundColor: isChecked ? '#4AB3BC' : '#fff',
  color: isChecked ? '#fff' : 'transparent',
  fontSize: '16px',
}));

// ============================================
// Guide Modal
// ============================================
const guideShine = keyframes`
  0%, 100% { background-position: -200% center; }
  50% { background-position: 200% center; }
`;

const guidePulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(74, 179, 188, 0.4); }
  50% { box-shadow: 0 0 0 6px rgba(74, 179, 188, 0); }
  100% { box-shadow: 0 0 0 0 rgba(74, 179, 188, 0); }
`;

export const GuideButton = styled(Button)({
  fontSize: '13px',
  fontWeight: 500,
  fontFamily: 'Pretendard, sans-serif',
  borderRadius: '20px',
  padding: '4px 14px',
  minWidth: 0,
  whiteSpace: 'nowrap',
  textTransform: 'none',
  background: 'linear-gradient(120deg, #4AB3BC 0%, #6DD5DC 40%, #fff 50%, #6DD5DC 60%, #4AB3BC 100%)',
  backgroundSize: '200% 100%',
  color: '#fff',
  border: '1px solid transparent',
  animation: `${guideShine} 3s ease-in-out 1s infinite, ${guidePulse} 2s ease-in-out 1s 3`,
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'linear-gradient(120deg, #3FA0A8 0%, #4AB3BC 100%)',
    border: '1px solid transparent',
    transform: 'scale(1.05)',
    color: '#fff',
  },
});

export const GuideModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 480,
  background: '#fff',
  borderRadius: '12px',
  overflow: 'hidden',
  outline: 'none',
  maxHeight: '85vh',
  display: 'flex',
  flexDirection: 'column',
});

export const GuideModalHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: '#333',
  padding: '14px 20px',
});

export const GuideModalTitle = styled(Typography)({
  color: '#fff',
  fontSize: '16px',
  fontWeight: 600,
  fontFamily: 'Pretendard, sans-serif',
});

export const GuideModalBody = styled(Box)({
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  overflowY: 'auto',
});

export const GuideSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
});

export const GuideSectionLabel = styled(Typography)({
  color: '#262626',
  fontSize: '15px',
  fontWeight: 600,
  fontFamily: 'Pretendard, sans-serif',
});

export const GuideText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 400,
  color: '#666',
  fontFamily: 'Pretendard, sans-serif',
  lineHeight: 1.7,
});

export const GuideDivider = styled(Box)({
  height: '1px',
  background: '#E8E8E8',
});

export const GuideStepBox = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  padding: '12px 16px',
  background: '#F7F9FA',
  borderRadius: '8px',
});

export const GuideStepNumber = styled(Box)({
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  background: '#4AB3BC',
  color: '#fff',
  fontSize: '13px',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

export const GuideStepText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 400,
  color: '#555',
  fontFamily: 'Pretendard, sans-serif',
  lineHeight: 1.6,
});
